import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { HERO_VIDEO } from "@/data/services";
import { ArrowRight } from "lucide-react";
import { fadeUp } from "./variants";
import { HomeSlider } from "./HomeSlider";

export const HeroSection = ({ onOrder }) => {
  // Only load the heavy hero video on larger screens; mobile uses the lightweight poster to avoid crashes/slow loads.
  const [showHeroVideo, setShowHeroVideo] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const update = () => setShowHeroVideo(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden grain bg-brand-dark">
      <div className="absolute inset-0 z-0">
        {showHeroVideo ? (
          <>
            <img src="/hero/hero-bg.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />
            <video
              src={HERO_VIDEO}
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              onCanPlay={() => setVideoReady(true)}
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ease-out ${videoReady ? "opacity-70" : "opacity-0"}`}
            />
          </>
        ) : (
          <img src="/hero/hero-bg.jpg" alt="" className="w-full h-full object-cover opacity-70" />
        )}
        <div className="absolute inset-0 bg-brand-dark/75" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-28 w-full">
        {/* Centered heading block */}
        <div className="text-center max-w-4xl mx-auto">
          <motion.h2 initial="hidden" animate="show" variants={fadeUp}
            className="text-3xl sm:text-5xl lg:text-6xl font-heading font-bold tracking-tight text-white mb-4 leading-tight">
            Tanz-<span className="text-brand-yellow">BRIDGE</span> International LTD
          </motion.h2>
          <motion.p initial="hidden" animate="show" custom={0.5} variants={fadeUp}
            className="text-xs tracking-[0.3em] uppercase text-brand-yellow mb-6">
            Commitment for Excellence
          </motion.p>
        </div>

        <HomeSlider />

        <div className="mt-14">
          <div className="text-left">
            <motion.p initial="hidden" whileInView="show" viewport={{ once: true }} custom={2} variants={fadeUp}
              className="text-base sm:text-lg text-gray-200 leading-relaxed text-left">
              Tanz – BRIDGE International LTD is an intermediate company providing Technical Consultancy
              Services in Tanzania on Production and Distribution, Information Technology, Research, Marketing
              and Trading on industrial and agricultural products, linking the producer and consumer on a
              global market.
            </motion.p>
            <motion.p initial="hidden" whileInView="show" viewport={{ once: true }} custom={2.5} variants={fadeUp}
              className="mt-4 text-base sm:text-lg text-gray-200 leading-relaxed text-left">
              Our mission is to encourage entrepreneurship, developing Enterprises and connect
              domestic producers to various internal and international markets.
            </motion.p>
            <motion.p initial="hidden" whileInView="show" viewport={{ once: true }} custom={2.8} variants={fadeUp}
              className="mt-4 text-base sm:text-lg text-gray-200 leading-relaxed text-left">
              Our vision is to provide effective utilization of labor by encouraging entrepreneurship, developing Enterprises,
              talents and employing different skills. To encourage productive usage of technology by our clients.
              To conduct seminars and connect domestic producers and social projects to various international
              agencies for training and capital access. To provide all facet of support for our clients on matters
              concerning IT, Research, Environmental and Technical Consultancies. We focus on quality work and
              clients satisfaction.
            </motion.p>
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} custom={3} variants={fadeUp}
              className="mt-8 flex flex-wrap gap-4 justify-center">
              <Button data-testid="hero-order-btn" onClick={onOrder}
                className="bg-brand-yellow hover:bg-brand-yellowhover text-brand-dark font-semibold rounded-sm px-8 py-6 text-base">
                Order a Service <ArrowRight size={18} className="ml-2" />
              </Button>
              <Button data-testid="hero-pricing-btn" onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })}
                variant="outline" className="border-white/30 hover:border-white bg-transparent text-white hover:bg-white/5 rounded-sm px-8 py-6 text-base">
                View Subscriptions
              </Button>
            </motion.div>
            <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} custom={3.5} variants={fadeUp}
              className="mt-12 grid grid-cols-3 gap-6 max-w-lg mx-auto text-center">
              {[["6", "Service areas"], ["2", "Global offices"], ["100%", "Client focus"]].map(([n, l]) => (
                <div key={l}>
                  <p className="text-3xl font-heading text-white">{n}</p>
                  <p className="text-xs text-gray-400 mt-1">{l}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
